/**
 * Markalarımız: sonsuz şerit + sürükleme
 * Masaüstü: küçük mouse titremesi tıklamayı engellemesin
 */
class MarkalarimizMarquee extends HTMLElement {
  #raf = 0;
  #offset = 0;
  #paused = false;
  #dragging = false;
  #moved = false;
  #suppressClick = false;
  #startX = 0;
  #startOffset = 0;
  #loopWidth = 0;
  #speed = 55;
  #direction = 1;
  #resumeTimer = 0;
  #lastTs = 0;
  #baseCount = 0;
  #ready = false;
  #activePointerId = null;

  connectedCallback() {
    this.track = this.querySelector("[data-track]");
    if (!this.track) return;

    const speedSetting = Number(this.dataset.speed || 25);
    this.#speed = Math.max(28, 100 - speedSetting * 1.1);
    this.#direction = this.dataset.direction === "normal" ? -1 : 1;
    this.#baseCount = this.track.children.length;
    if (!this.#baseCount) return;

    this.style.cursor = "grab";
    this.addEventListener("pointerdown", this.#onPointerDown);
    window.addEventListener("pointermove", this.#onPointerMove);
    window.addEventListener("pointerup", this.#onPointerUp);
    window.addEventListener("pointercancel", this.#onPointerUp);
    window.addEventListener("resize", this.#onResize);
    this.addEventListener("click", this.#onClick, true);

    this.#setup().then(() => {
      this.#ready = true;
      if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.#lastTs = performance.now();
        this.#tick(this.#lastTs);
      } else {
        this.#render();
      }
    });
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#raf);
    clearTimeout(this.#resumeTimer);
    window.removeEventListener("pointermove", this.#onPointerMove);
    window.removeEventListener("pointerup", this.#onPointerUp);
    window.removeEventListener("pointercancel", this.#onPointerUp);
    window.removeEventListener("resize", this.#onResize);
  }

  async #setup() {
    await this.#waitForImages();
    this.#balanceLogos();
    this.#fillTrack();
    this.#measureLoop();
    this.#offset = 0;
    this.#render();
  }

  /**
   * Farklı en-boy oranlarını aynı görsel alana yaklaştırır.
   * (ESR gibi geniş logolar küçülür, kare logolar büyür — marka özel kural yok)
   */
  #balanceLogos() {
    const root = this.closest(".markalarimiz") || this;
    const slotH =
      parseFloat(getComputedStyle(root).getPropertyValue("--marka-logo-height")) ||
      64;
    const refAspect = 2.6;
    const targetArea = slotH * slotH * refAspect;
    const maxW = slotH * 4.5;
    const maxH = slotH;
    const minH = slotH * 0.62;

    const imgs = [...this.track.querySelectorAll(".markalarimiz__img")];
    for (const img of imgs) {
      const nw = img.naturalWidth || 0;
      const nh = img.naturalHeight || 0;
      if (!nw || !nh) continue;

      const ar = nw / nh;
      let h = Math.sqrt(targetArea / ar);
      let w = h * ar;

      if (w > maxW) {
        w = maxW;
        h = w / ar;
      }
      if (h > maxH) {
        h = maxH;
        w = h * ar;
      }
      if (h < minH) {
        h = minH;
        w = h * ar;
        if (w > maxW) {
          w = maxW;
          h = w / ar;
        }
      }

      img.style.width = `${Math.round(w * 10) / 10}px`;
      img.style.height = `${Math.round(h * 10) / 10}px`;
    }
  }

  #waitForImages() {
    const images = [...this.track.querySelectorAll("img")];
    return Promise.all(
      images.map((img) => {
        if (img.complete && img.naturalWidth) return Promise.resolve();
        return (
          img.decode?.().catch(() => {}) ||
          new Promise((resolve) => {
            img.addEventListener("load", resolve, { once: true });
            img.addEventListener("error", resolve, { once: true });
          })
        );
      })
    );
  }

  #fillTrack() {
    while (this.track.children.length > this.#baseCount) {
      this.track.lastElementChild?.remove();
    }

    const originals = [...this.track.children];
    let guard = 0;
    while (guard < 30) {
      const target = Math.max(this.clientWidth * 3, 1);
      if (
        this.track.scrollWidth >= target &&
        this.track.children.length >= this.#baseCount * 2
      ) {
        break;
      }
      for (const node of originals) {
        this.track.appendChild(node.cloneNode(true));
      }
      guard += 1;
    }
  }

  #measureLoop() {
    const first = this.track.children[0];
    const next = this.track.children[this.#baseCount];
    if (!first || !next) {
      this.#loopWidth = Math.max(1, this.track.scrollWidth / 2);
      return;
    }
    this.#loopWidth = next.offsetLeft - first.offsetLeft;
    if (this.#loopWidth <= 0) {
      this.#loopWidth = Math.max(1, this.track.scrollWidth / 2);
    }
  }

  #onResize = () => {
    if (!this.#ready) return;
    const progress = this.#loopWidth ? Math.abs(this.#offset) / this.#loopWidth : 0;
    // Klonları temizle, orijinalleri yeniden dengele, tekrar doldur
    while (this.track.children.length > this.#baseCount) {
      this.track.lastElementChild?.remove();
    }
    this.#balanceLogos();
    this.#fillTrack();
    this.#measureLoop();
    this.#offset = this.#wrapOffset(-progress * this.#loopWidth);
    this.#render();
  };

  #wrapOffset(value) {
    if (!this.#loopWidth) return 0;
    let v = value % this.#loopWidth;
    if (v > 0) v -= this.#loopWidth;
    if (v <= -this.#loopWidth) v += this.#loopWidth;
    return v;
  }

  #render() {
    this.track.style.transform = `translate3d(${this.#offset}px, 0, 0)`;
  }

  #tick = (ts) => {
    this.#raf = requestAnimationFrame(this.#tick);
    if (!this.#ready || !this.#loopWidth) return;

    const dt = Math.min(0.064, (ts - this.#lastTs) / 1000 || 0);
    this.#lastTs = ts;

    if (this.#paused || this.#dragging) return;

    this.#offset -= this.#speed * dt * this.#direction;
    this.#offset = this.#wrapOffset(this.#offset);
    this.#render();
  };

  #onPointerDown = (event) => {
    if (event.button != null && event.button !== 0) return;
    this.#dragging = true;
    this.#moved = false;
    this.#suppressClick = false;
    this.#activePointerId = event.pointerId;
    this.#paused = true;
    this.#startX = event.clientX;
    this.#startOffset = this.#offset;
    this.style.cursor = "grabbing";
    clearTimeout(this.#resumeTimer);
    // setPointerCapture sürükleme başlayınca — erken capture tıklamayı bozuyor
  };

  #onPointerMove = (event) => {
    if (!this.#dragging) return;
    if (
      this.#activePointerId != null &&
      event.pointerId !== this.#activePointerId
    ) {
      return;
    }
    const dx = event.clientX - this.#startX;
    // Masaüstü mouse titremesi için daha yüksek eşik
    if (!this.#moved && Math.abs(dx) > 10) {
      this.#moved = true;
      this.#suppressClick = true;
      try {
        this.setPointerCapture(event.pointerId);
      } catch (_) {}
    }
    if (!this.#moved) return;
    this.#offset = this.#wrapOffset(this.#startOffset + dx);
    this.#render();
  };

  #onPointerUp = (event) => {
    if (!this.#dragging) return;
    if (
      this.#activePointerId != null &&
      event.pointerId != null &&
      event.pointerId !== this.#activePointerId
    ) {
      return;
    }
    this.#dragging = false;
    this.#activePointerId = null;
    this.style.cursor = "grab";
    try {
      if (event.pointerId != null) this.releasePointerCapture(event.pointerId);
    } catch (_) {}
    this.#resumeTimer = window.setTimeout(() => {
      this.#paused = false;
      this.#lastTs = performance.now();
    }, 700);
  };

  #onClick = (event) => {
    // Sadece gerçek sürüklemede linki engelle
    if (this.#suppressClick || this.#moved) {
      event.preventDefault();
      event.stopPropagation();
      this.#suppressClick = false;
      this.#moved = false;
      return;
    }
    // Saf tıklama: <a> doğal gitsin
  };
}

if (!customElements.get("markalarimiz-marquee")) {
  customElements.define("markalarimiz-marquee", MarkalarimizMarquee);
}
